<template>
    <ColorSizeSelectorWrapper
        :id="id"
        class="u-spacer--1"
        :colorCode="colorCode"
        :choices="colorChoices"
        :sizeCodeAvailibilityByChoiceId="sizeCodeAvailibilityByChoiceId"
        :selectedChoice="selectedChoice"
        :selectedSku="selectedSku"
        :selectedSkuSizeCode="selectedSkuSizeCode"
        :addToBoxError="addToBoxError"
        :purchasableSkus="purchasableSkus"
        :reviewSummary="reviewSummary"
        :setSkuCode="setSkuCode"
        :setSkuSizeCode="setSkuSizeCode"
        :setErrorText="setErrorText"
        :updateItemInBoxStatus="updateItemInBoxStatus"
        :updateSelectedColor="updateSelectedColor"
    />
</template>

<script setup>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useState, useGetters, useMutations, useActions } from '~coreModules/core/js/composables/vuexHelpers';

import { getProductModuleNameFromRoute } from '~coreRoutes/product/js/product-utils';
import {
    SET_SKU_CODE,
    SET_SKU_SIZE_CODE,
    SET_ADD_TO_BOX_ERROR,
    UPDATE_ITEM_IN_BOX_STATUS,
    SET_COLOR_CODE,
} from '~routes/product/js/product-store';
import { MODALS_MODULE_NAME, POP_MODALS } from '~coreModules/modals/js/modals-store';

import ColorSizeSelectorWrapper from '~modules/core/components/ui/ColorSizeSelectorWrapper.vue';

defineProps({
    id: {
        type: String,
        required: true,
    },
    selectedChoice: {
        type: Object,
        required: true,
    },
    colorChoices: {
        type: Array,
        required: true,
    },
    sizeCodeAvailibilityByChoiceId: {
        type: Object,
        required: true,
    },
    colorCode: {
        type: String,
        required: true,
    },
    reviewSummary: {
        type: Object,
        default: () => ({}),
    },
});

const route = useRoute();
const router = useRouter();

const productModuleName = computed(() => getProductModuleNameFromRoute(route));
const {
    selectedSku,
    selectedSkuSizeCode,
    addToBoxError,
} = useState(productModuleName.value, {
    selectedSku: state => state.selectedSku,
    selectedSkuSizeCode: state => state.selectedSkuSizeCode,
    addToBoxError: state => state.addToBoxError,
});

const { purchasableSkus } = useGetters(productModuleName.value, ['purchasableSkus']);

const {
    setSkuCode,
    setColorCode,
    setSkuSizeCode,
    setErrorText,
    updateItemInBoxStatus,
} = useMutations(productModuleName.value, {
    setSkuCode: SET_SKU_CODE,
    setColorCode: SET_COLOR_CODE,
    setSkuSizeCode: SET_SKU_SIZE_CODE,
    setErrorText: SET_ADD_TO_BOX_ERROR,
    updateItemInBoxStatus: UPDATE_ITEM_IN_BOX_STATUS,
});

function updateSelectedColor(color) {
    router.replace({
        query: {
            color,
            ...(route.query?.view && {
                view: route.query?.view,
            }),
        },
    });
    setColorCode(color);
}

const { popModals } = useActions(MODALS_MODULE_NAME, { popModals: POP_MODALS });

watch(() => route.query.sku, (newSku, oldSku) => {
    // if there is both a previous and next value, we need to pop the modals here
    // this means that the user clicked another box tile for a different size
    if (newSku && newSku !== oldSku) {
        popModals();
        setSkuCode(newSku);
    }
}, { immediate: true });

</script>
